import { useEffect, useRef, useState } from 'react'
import { EMPTY_FILTER, STATUSES, STATUS_LABELS, LINK_HEALTH_STATES } from '../util.js'

// Labels for the states util.js exports, plus the two values that are not
// crawler-written states: '' (no constraint) and 'unchecked' (the filter's
// name for a listing whose linkHealth has never been set — see
// applyViewFilter). Built off LINK_HEALTH_STATES rather than a hand-typed
// list of values, so this can't drift from the states util.js knows about.
const LINK_HEALTH_LABELS = {
  ok: 'Link OK',
  missing: 'Link missing',
  dead: 'Link dead',
  blocked: 'Blocked',
  'robots-skipped': 'Not checkable',
  error: 'Check failed',
}

const HEALTH_OPTIONS = [
  ['', 'Any link state'],
  ...LINK_HEALTH_STATES.map((s) => [s, LINK_HEALTH_LABELS[s]]),
  ['unchecked', 'Never checked'],
]

const NUMBERS = [
  ['priceMax', 'Max price'],
  ['mileageMax', 'Max mileage'],
  ['yearMin', 'Year from'],
  ['yearMax', 'Year to'],
  ['distanceMax', 'Max distance'],
]

const isActive = (f) => JSON.stringify(f) !== JSON.stringify(EMPTY_FILTER)

const draftFrom = (filter) => ({ ...EMPTY_FILTER, ...(filter || {}) })

// EMPTY_FILTER's own key order, used as the canonical field order everywhere
// two filter objects need comparing. A caller's object — rebuilt from form
// fields, a URL round-trip, storage — can have any key order; iterating our
// own order rather than the incoming object's means that never matters.
const FIELDS = Object.keys(EMPTY_FILTER)
const fieldValues = (filter) => {
  const f = draftFrom(filter)
  return FIELDS.map((k) => f[k])
}
const sameFilter = (a, b) => {
  const av = fieldValues(a)
  const bv = fieldValues(b)
  return av.every((v, i) => v === bv[i])
}
// A string built from the canonical field values, not JSON.stringify(filter)
// on the raw prop — so an object with the same values but a different key
// order never reads as a change to this dependency.
const canonicalKey = (filter) => JSON.stringify(fieldValues(filter))

export default function FilterBar({ filter, onChange, shown, total }) {
  // Local draft, not a plain read of `filter`: a controlled input whose value
  // never advances between keystrokes reverts to the stale prop the instant
  // React re-syncs the DOM after each `input` event, so a caller that doesn't
  // echo every keystroke straight back down (this component's own tests,
  // and any parent that batches) would only ever see the last character
  // typed. The draft accumulates locally and is the single source the inputs
  // render from; `onChange` still fires on every keystroke so a parent that
  // does want to hold this in its own state can.
  const [draft, setDraft] = useState(() => draftFrom(filter))

  // The last filter object handed to onChange, so the effect below can skip
  // reseeding when the incoming `filter` is value-equal to what this
  // component just sent — its own echo, not an external change.
  //
  // What this buys, precisely: against a parent that holds `filter` in state
  // and echoes it back synchronously and unmodified (the shape Task 13's
  // crawl-criteria hookup is required to have), the skip is not observable —
  // reseeding to the same values and skipping the reseed render identically,
  // so there is nothing here a synchronous test could ever catch failing.
  // What it does not buy: a single `lastSent` snapshot has no history, so it
  // cannot tell a *stale* echo of an earlier keystroke apart from a genuine
  // external change. Against an async or normalising parent — one that
  // clamps/rounds a value, or echoes on a delay — a stale echo landing after
  // further typing reads as "different from the latest snapshot" and reseeds
  // backward over what the operator already typed. Closing that would need a
  // generation counter or a short history of sent values, not a tweak to this
  // ref. Not attempted here because no such parent exists yet to be broken by
  // it, and Task 13 is required to be the synchronous, faithful-echo shape
  // this fix does cover.
  const lastSentRef = useRef(filter)

  useEffect(() => {
    if (sameFilter(filter, lastSentRef.current)) return
    lastSentRef.current = filter
    setDraft(draftFrom(filter))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalKey(filter)])

  const commit = (next) => {
    lastSentRef.current = next
    setDraft(next)
    onChange?.(next)
  }

  // Empty means "no constraint", so it must become null rather than 0 — a 0
  // ceiling would hide everything.
  const setNum = (key) => (e) =>
    commit({ ...draft, [key]: e.target.value === '' ? null : Number(e.target.value) })
  const setStr = (key) => (e) => commit({ ...draft, [key]: e.target.value })
  const setBool = (key) => (e) => commit({ ...draft, [key]: e.target.checked })

  return (
    <div className="filters">
      <div className="filter-row">
        <label className="grow">
          Search
          <input
            type="search"
            value={draft.text}
            onChange={setStr('text')}
            placeholder="Title, dealer, city, stock, VIN"
          />
        </label>
        {NUMBERS.map(([key, label]) => (
          <label key={key}>
            {label}
            <input type="number" value={draft[key] ?? ''} onChange={setNum(key)} />
          </label>
        ))}
      </div>

      <div className="filter-row">
        <label>
          Status
          <select value={draft.status} onChange={setStr('status')}>
            <option value="">Any status</option>
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </label>
        <label>
          Dealer link
          <select value={draft.linkHealth} onChange={setStr('linkHealth')}>
            {HEALTH_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={draft.followUpDue} onChange={setBool('followUpDue')} />
          Follow-up due
        </label>

        <span className="spacer" />
        <span className="count-line">Showing {shown} of {total}</span>
        {isActive(draft) && (
          <button className="link-btn" onClick={() => commit(EMPTY_FILTER)}>Clear filters</button>
        )}
      </div>

      <p className="hint">
        A view filter — it does not change what matches or alerts. Use Search settings for that.
      </p>
    </div>
  )
}
